import React, { ReactElement, useEffect } from 'react';
import { Typography, styled } from '@mui/material';
import { ProgressBlocks, setClientProgressBlock } from '../../redux/client/clientSlice';
import ClientProgress from './ClientProgress';
import SingleActivityProgress from './SingleActivityProgress';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { ActivityTypeEnum } from '../../types/activity';
import i18n from '../../i18n';

const progressBlocks: Record<ProgressBlocks, ReactElement> = {
  progress: <ClientProgress />,
  activity: <SingleActivityProgress />,
};

interface IStyledTypographyProps {
  backgroundColor?: string;
  textColor?: string;
}

const StyledTypography = styled(Typography)<IStyledTypographyProps>(
  ({ backgroundColor, textColor }) => ({
    borderRadius: 5,
    background: backgroundColor,
    padding: '4px 8px',
    textAlign: 'center',
    '& span': {
      background: textColor,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  })
);

export const renderActivitySchedule = (schedule: ActivityTypeEnum) => {
  switch (schedule) {
    case ActivityTypeEnum.SITUATIONAL:
      return (
        <StyledTypography
          variant="tinyTexts"
          fontWeight={700}
          backgroundColor="linear-gradient(175deg, rgba(255, 203, 203, 0.15) -9.81%, 
            rgba(255, 127, 127, 0.15) 96.32%)"
          textColor="var(--Alert-Sunset-Orange, linear-gradient(175deg, #FF9A9A -9.81%, #FF5858 96.32%))"
          paddingX={8}
        >
          <span>{i18n.t('clientProgress.situational')}</span>
        </StyledTypography>
      );
    case ActivityTypeEnum.RECCURENT:
      return (
        <StyledTypography
          variant="tinyTexts"
          fontWeight={700}
          backgroundColor="linear-gradient(175deg, rgba(146, 207, 205, 0.15) -9.81%, 
            rgba(51, 110, 107, 0.15) 96.32%)"
          textColor="var(--Alert-Myrtle-Green, linear-gradient(175deg, #92CFCD -9.81%, #336E6B 96.32%))"
          paddingX={8}
        >
          <span>{i18n.t('clientProgress.repetitive')}</span>
        </StyledTypography>
      );
    case ActivityTypeEnum.ONETIME:
      return (
        <StyledTypography
          variant="tinyTexts"
          fontWeight={700}
          backgroundColor="linear-gradient(175deg, rgba(87, 177, 90, 0.15) -9.81%, 
            rgba(47, 125, 50, 0.15) 96.32%)"
          textColor="var(--Alert-Japanese-Laurel, linear-gradient(175deg, #57B15A -9.81%, #2F7D32 96.32%))"
          paddingX={8}
        >
          <span>{i18n.t('clientProgress.oneTime')}</span>
        </StyledTypography>
      );
    default:
      return <Typography>{String(schedule)}</Typography>;
  }
};

function Progress() {
  const currentBlock = useAppSelector((state) => state.client.block);
  const dispatch = useAppDispatch();

  useEffect(
    () => () => {
      dispatch(setClientProgressBlock());
    },
    []
  );

  return progressBlocks[currentBlock];
}

export default Progress;
